import React, { createContext, useContext, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { ElectronicsConfiguration } from "../models/electronicsConfiguration.ts";
import { Signal, SignalSourceType } from "../models/signal.ts";

interface ElectronicsConfigurationContextType {
  config: ElectronicsConfiguration;
  setConfig: (config: ElectronicsConfiguration) => void;
  handleChange: (section: keyof ElectronicsConfiguration, value: any) => void;
}

const withSource = <T extends Signal>(
  signal: T,
  source_type: SignalSourceType,
  source_id?: string,
): T => ({
  ...signal,
  source_type,
  source_id: source_id ?? signal.id,
});

const createI2cConfig = (label: "I2C 0" | "I2C 1") => {
  const key = label === "I2C 0" ? "i2c0" : "i2c1";
  return {
    label,
    enable: false,
    sda: withSource({ name: label, net: "SDA", id: uuidv4() }, "communication", `communication.${key}.sda`),
    scl: withSource({ name: label, net: "SCL", id: uuidv4() }, "communication", `communication.${key}.scl`),
  };
};

const createLabChannel = (channel: 1 | 2 | 3) => ({
  positive: withSource(
    {
      id: uuidv4(),
      net: `Ext. Power Supply Channel ${channel} VCC`,
      name: `Kanal ${channel}`,
    },
    "external_supply",
    `power_supply.labPowerSupply.channel${channel}.positive`,
  ),
});

const createLabPowerSupply = () => ({
  label: "Labornetzteil",
  enable: false,
  channels: 1 as const,
  channel1: createLabChannel(1),
  channel2: createLabChannel(2),
  channel3: createLabChannel(3),
  ground: withSource(
    { id: uuidv4(), net: "Ext. Power Supply GND", name: "GND" },
    "ground",
    "power_supply.labPowerSupply.ground",
  ),
});

const normalizeLabChannel = (
  channel: ReturnType<typeof createLabChannel> | undefined,
  channelNumber: 1 | 2 | 3,
) => {
  const defaults = createLabChannel(channelNumber);
  return {
    positive: {
      ...defaults.positive,
      ...(channel?.positive ?? {}),
      net: `Ext. Power Supply Channel ${channelNumber} VCC`,
      source_type: "external_supply" as const,
      source_id: `power_supply.labPowerSupply.channel${channelNumber}.positive`,
    },
  };
};

const normalizeLabPowerSupply = (
  labPowerSupply: ElectronicsConfiguration["power_supply"]["labPowerSupply"] | undefined,
) => {
  const defaults = createLabPowerSupply();
  const normalized = {
    ...defaults,
    ...(labPowerSupply ?? {}),
    ground: {
      ...defaults.ground,
      ...(labPowerSupply?.ground ?? {}),
      net: "Ext. Power Supply GND",
      source_type: "ground" as const,
      source_id: "power_supply.labPowerSupply.ground",
    },
  };
  return {
    ...normalized,
    channel1: normalizeLabChannel(labPowerSupply?.channel1, 1),
    channel2: normalizeLabChannel(labPowerSupply?.channel2, 2),
    channel3: normalizeLabChannel(labPowerSupply?.channel3, 3),
  };
};

const defaultConfig: ElectronicsConfiguration = {
  max_modules: 2,
  power_supply: {
    dcdc24V: withSource({ label: "DC/DC 24V ±5% 1A", enable: false, id: uuidv4(), name: "VCC", net: "24V [DCDC]" }, "power_supply", "power_supply.dcdc24V"),
    dcdc12V: withSource({ label: "DC/DC 12V ±0,4% 800mA", enable: false, id: uuidv4(), name: "VCC", net: "12V [DCDC]" }, "power_supply", "power_supply.dcdc12V"),
    dcdc5V: withSource({ label: "DC/DC 5V ±2,5% 1,5A", enable: false, id: uuidv4(), name: "VCC", net: "5V [DCDC]" }, "power_supply", "power_supply.dcdc5V"),
    dcdc3V3: withSource({ label: "DC/DC 3,3V ±2% 500mA", enable: false, id: uuidv4(), name: "VCC", net: "3V3 [DCDC]" }, "power_supply", "power_supply.dcdc3V3"),
    linear1: withSource({ label: "Linearregler 1 ±2% 150mA", enable: false, volt: 2, id: uuidv4(), name: "VCC", net: "2.00V [L1]" }, "power_supply", "power_supply.linear1"),
    linear2: withSource({ label: "Linearregler 2 ±2% 150mA", enable: false, volt: 3, id: uuidv4(), name: "VCC", net: "3.00V [L2]" }, "power_supply", "power_supply.linear2"),
    linear3: withSource({ label: "Linearregler 3 ±2% 150mA", enable: false, volt: 4, id: uuidv4(), name: "VCC", net: "4.00V [L3]" }, "power_supply", "power_supply.linear3"),
    dcdcAdjustable1: withSource({ label: "DCDC 1 ±5% 1A", enable: false, volt: 10, id: uuidv4(), name: "VCC", net: "10.00V [DCDC1]" }, "power_supply", "power_supply.dcdcAdjustable1"),
    dcdcAdjustable2: withSource({ label: "DCDC 2 ±5% 1A", enable: false, volt: 11, id: uuidv4(), name: "VCC", net: "11.00V [DCDC2]" }, "power_supply", "power_supply.dcdcAdjustable2"),
    dcdcAdjustable3: withSource({ label: "DCDC 3 ±5% 1A", enable: false, volt: 12, id: uuidv4(), name: "VCC", net: "12.00V [DCDC3]" }, "power_supply", "power_supply.dcdcAdjustable3"),
    labPowerSupply: createLabPowerSupply(),
    ground: withSource({ id: uuidv4(), name: "Testelectronics", net: "GND" }, "ground", "power_supply.ground"),
  },
  communication: {
    rs232: {
      label: "RS232",
      enable: false,
      rx: withSource({ name: "RS232", net: "RX", id: uuidv4() }, "communication", "communication.rs232.rx"),
      tx: withSource({ name: "RS232", net: "TX", id: uuidv4() }, "communication", "communication.rs232.tx"),
    },
    rs485: {
      label: "RS485",
      enable: false,
      a: withSource({ name: "RS485", net: "A", id: uuidv4() }, "communication", "communication.rs485.a"),
      b: withSource({ name: "RS485", net: "B", id: uuidv4() }, "communication", "communication.rs485.b"),
    },
    uart: {
      label: "UART (3V3)",
      enable: false,
      rx: withSource({ name: "UART", net: "RX", id: uuidv4() }, "communication", "communication.uart.rx"),
      tx: withSource({ name: "UART", net: "TX", id: uuidv4() }, "communication", "communication.uart.tx"),
    },
    i2c0: createI2cConfig("I2C 0"),
    i2c1: createI2cConfig("I2C 1"),
    usb: {
      label: "USB 2.0 (5V)",
      enable: false,
      otg: withSource({ name: "USB2.0", net: "OTG", id: uuidv4() }, "communication", "communication.usb.otg"),
      dp: withSource({ name: "USB2.0", net: "DP", id: uuidv4() }, "communication", "communication.usb.dp"),
      dn: withSource({ name: "USB2.0", net: "DN", id: uuidv4() }, "communication", "communication.usb.dn"),
    },
    sd_card: {
      label: "SD-Karte",
      enable: false,
      clk: withSource({ name: "SD-Karte", net: "CLK", id: uuidv4() }, "communication", "communication.sd_card.clk"),
      d0: withSource({ name: "SD-Karte", net: "D0", id: uuidv4() }, "communication", "communication.sd_card.d0"),
      d1: withSource({ name: "SD-Karte", net: "D1", id: uuidv4() }, "communication", "communication.sd_card.d1"),
      d2: withSource({ name: "SD-Karte", net: "D2", id: uuidv4() }, "communication", "communication.sd_card.d2"),
      d3: withSource({ name: "SD-Karte", net: "D3", id: uuidv4() }, "communication", "communication.sd_card.d3"),
    },
  },
  flasher: {
    stlink1: {
      label: "ST-Link Nr. 1",
      enable: false,
      target_voltage: withSource({ id: uuidv4(), net: "VCC Target", name: "ST-Link 1" }, "communication", "flasher.stlink1.target_voltage"),
      swdio_tms: withSource({ id: uuidv4(), net: "SWDIO / TMS", name: "ST-Link 1" }, "communication", "flasher.stlink1.swdio_tms"),
      swclk_tck: withSource({ id: uuidv4(), net: "SWCLK / TCK", name: "ST-Link 1" }, "communication", "flasher.stlink1.swclk_tck"),
      swo_tdo: withSource({ id: uuidv4(), net: "SWO / TDO", name: "ST-Link 1" }, "communication", "flasher.stlink1.swo_tdo"),
      tdi: withSource({ id: uuidv4(), net: "TDI", name: "ST-Link 1" }, "communication", "flasher.stlink1.tdi"),
      ntrst: withSource({ id: uuidv4(), net: "nTRST", name: "ST-Link 1" }, "communication", "flasher.stlink1.ntrst"),
      nrst: withSource({ id: uuidv4(), net: "NRST", name: "ST-Link 1" }, "communication", "flasher.stlink1.nrst"),
    },
    stlink2: {
      label: "ST-Link Nr. 2",
      enable: false,
      target_voltage: withSource({ id: uuidv4(), name: "VCC Target", net: "ST-Link 2" }, "communication", "flasher.stlink2.target_voltage"),
      swdio_tms: withSource({ id: uuidv4(), name: "SWDIO / TMS", net: "ST-Link 2" }, "communication", "flasher.stlink2.swdio_tms"),
      swclk_tck: withSource({ id: uuidv4(), name: "SWCLK / TCK", net: "ST-Link 2" }, "communication", "flasher.stlink2.swclk_tck"),
      swo_tdo: withSource({ id: uuidv4(), name: "SWO / TDO", net: "ST-Link 2" }, "communication", "flasher.stlink2.swo_tdo"),
      tdi: withSource({ id: uuidv4(), name: "TDI", net: "ST-Link 2" }, "communication", "flasher.stlink2.tdi"),
      ntrst: withSource({ id: uuidv4(), name: "nTRST", net: "ST-Link 2" }, "communication", "flasher.stlink2.ntrst"),
      nrst: withSource({ id: uuidv4(), name: "NRST", net: "ST-Link 2" }, "communication", "flasher.stlink2.nrst"),
    },
  },
  custom_signals: {
    resistor: [],
    schottky: [],
  },
  signal_mapping: {
    relais: [],
    voltage: [],
    current: [],
    two_quadrant: [],
    resistor: [],
  },
  modules: {
    relais: 0,
    voltage: 0,
    current: 0,
    two_quadrant: 0,
  },
  testpoints: [],
};

const normalizeElectronicsConfiguration = (
  configuration: ElectronicsConfiguration,
): ElectronicsConfiguration => ({
  ...configuration,
  power_supply: {
    ...configuration.power_supply,
    labPowerSupply: normalizeLabPowerSupply(configuration.power_supply.labPowerSupply),
  },
  communication: {
    ...configuration.communication,
    i2c0: configuration.communication.i2c0 ?? createI2cConfig("I2C 0"),
    i2c1: configuration.communication.i2c1 ?? createI2cConfig("I2C 1"),
  },
});

const ElectronicsConfigurationContext = createContext<ElectronicsConfigurationContextType | undefined>(undefined);

export const useElectronicsConfiguration = () => {
  const context = useContext(ElectronicsConfigurationContext);
  if (!context) {
    throw new Error("useElectronicsConfiguration must be used within a ElectronicsConfigurationProvider");
  }
  return context;
};

export const ElectronicsConfigurationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [config, setConfigState] = useState<ElectronicsConfiguration>(defaultConfig);

  const setConfig = (updatedConfig: ElectronicsConfiguration) => {
    setConfigState(normalizeElectronicsConfiguration(updatedConfig));
  };

  const handleChange = (section: keyof ElectronicsConfiguration, value: any) => {
    setConfigState(prev => normalizeElectronicsConfiguration({ ...prev, [section]: value }));
  };

  return (
    <ElectronicsConfigurationContext.Provider value={{ config, setConfig, handleChange }}>
      {children}
    </ElectronicsConfigurationContext.Provider>
  );
};
